/**
 * **The ornamental frame around the menu and the lobby, one per people.**
 *
 * A nine-slice border drawn as character-grid art, exactly like every sprite in
 * the game, and turned into a `border-image` data URL at boot by `frameUrl`.
 * No files: the frame is a few hundred bytes of source and a canvas.
 *
 * **Why it is built out of 8 × 8 cells.** `border-image` cuts a source into
 * nine pieces and *repeats* the four edge pieces along their sides, so the
 * middle slice has to be a tile that can sit beside a copy of itself without a
 * visible seam. Making the whole thing a grid of self-contained riveted plates
 * solves that by construction — a plate next to a plate is a longer wall, at
 * any panel width, with no arithmetic. The corners carry a larger stone so the
 * eye has somewhere to land.
 *
 * The centre cell is blank because `border-image` throws the middle away unless
 * asked not to; drawing anything there would be work nobody ever sees.
 *
 * Four tones, and only four, so a frame reads as one object: `#` the outline,
 * `d` the shadowed body, `m` the lit body, `y` the stone or rivet. The tones
 * change per people, the drawing does not — which is what keeps three themes
 * looking like three moods of one game rather than three different games.
 */

export const FRAME_W = 24;
/** One cell of the nine-slice, in source pixels. */
export const FRAME_SLICE = 8;

const BIG = [
  '########',
  '#dddddd#',
  '#dmmmmd#',
  '#dyyyyd#',
  '#dyyyyd#',
  '#dmmmmd#',
  '#dddddd#',
  '########',
];
const SMALL = [
  '########',
  '#dddddd#',
  '#dmmmmd#',
  '#dmyymd#',
  '#dmyymd#',
  '#dmmmmd#',
  '#dddddd#',
  '########',
];
const NONE = '        ';

/** corner · repeating edge · corner, then edge · nothing · edge, then the top again */
export const ART_FRAME: string[] = [
  ...BIG.map((r, i) => r + SMALL[i] + r),
  ...SMALL.map(r => r + NONE + r),
  ...BIG.map((r, i) => r + SMALL[i] + r),
];

/**
 * `[outline, shadow, lit, stone]` per `Race`.
 *
 * Human — a crimson band with gold studs, which is the colour a crown is drawn
 * in and the one the game already uses for its own accents.
 * Dwarf — quarried grey with bronze rivets: the frame of something built rather
 * than embroidered.
 * Elf — carved green wood with a pale leaf, lighter in weight than the other
 * two because nothing an elf makes is meant to look heavy.
 */
export const FRAME_TONES: string[][] = [
  ['#2a1206', '#6d1f1f', '#a83a2c', '#ffd85e'],
  ['#1b1a18', '#45413a', '#8d8574', '#c88a4a'],
  ['#10200e', '#2c4a26', '#4f9e4a', '#cfe08a'],
];
